import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function OccupationalTherapy() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/occupational-therapy#webpage`,
    url: `${siteUrl}/services/occupational-therapy`,
    name: 'Occupational Therapy at Home Las Vegas | Home Health',
    description: 'Home-based occupational therapy in Las Vegas — ADL training, adaptive equipment, home safety assessments, and cognitive rehabilitation. Medicare certified, ACHC accredited.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'OccupationalTherapy',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Occupational Therapy', item: `${siteUrl}/services/occupational-therapy` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Occupational Therapy at Home Las Vegas | Home Health';
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-occupational-therapy" />
      <Header />
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/37758bfc48f50cdfc912dfffa5597dfd.jpg" alt="Occupational Therapy" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6"><i className="ri-hand-heart-line"></i> Occupational Therapy</div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Occupational Therapy<br /><span className="text-amber-400">At Home</span> in Las Vegas</h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-8">Our licensed occupational therapists help you regain independence in daily activities — from cooking and dressing to home safety. Medicare certified and ACHC accredited.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request OT Today</a>
              <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i> 866-939-0330</a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">Our OT Services</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Occupational Therapy Includes</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">We help you live as independently and safely as possible in your own home.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-home-heart-line', title: 'Home Safety Assessments', desc: 'Comprehensive evaluation of your home to identify hazards and recommend modifications.' },
              { icon: 'ri-tools-line', title: 'Adaptive Equipment', desc: 'Recommendations and training on assistive devices like grab bars, reachers, and dressing aids.' },
              { icon: 'ri-user-line', title: 'ADL Training', desc: 'Hands-on coaching for bathing, dressing, grooming, cooking, and other daily living tasks.' },
              { icon: 'ri-brain-line', title: 'Cognitive Rehabilitation', desc: 'Targeted exercises to improve memory, attention, problem-solving, and executive function.' },
              { icon: 'ri-hand-heart-line', title: 'Fine Motor Skills', desc: 'Exercises to restore hand strength, coordination, and dexterity after injury or illness.' },
              { icon: 'ri-battery-charge-line', title: 'Energy Conservation', desc: 'Strategies to manage fatigue and accomplish daily tasks more efficiently.' },
            ].map((item, i) => (
              <div key={i} className="bg-stone-50 p-7 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4"><i className={`${item.icon} text-amber-700 text-lg`}></i></div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Regain Your Independence</h2>
          <p className="text-slate-400 text-lg mb-8">Contact Celebrity Home Health to schedule an occupational therapy evaluation at your Las Vegas area home.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Care Now</a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">View All Services</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
